import type { MessageKey } from "./messages";
import { translate } from "./messages";

const API_ERROR_KEYS: Record<string, MessageKey> = {
  document_not_found: "errors.documentNotFound",
  file_too_large: "errors.fileTooLarge",
  invalid_source_document_id: "errors.invalidSourceDocumentId",
  source_document_not_found: "errors.sourceDocumentNotFound",
  extraction_encrypted_only: "errors.extractionEncryptedOnly",
  extraction_status_conflict: "errors.extractionStatusConflict",
  legacy_plaintext_blob: "errors.legacyPlaintextBlob",
  encrypted_metadata_incomplete: "errors.encryptedMetadataIncomplete",
  storage_load_failed: "errors.storageLoadFailed",
  legacy_plaintext_file: "errors.legacyPlaintextFile",
  query_timed_out: "errors.queryTimedOut",
  unsupported_locale: "errors.unsupportedLocale",
  reminder_not_found: "errors.reminderNotFound",
  rate_limit_exceeded: "errors.rateLimitExceeded",
  crypto_already_configured: "errors.cryptoAlreadyConfigured",
  stripe_not_configured: "errors.stripeNotConfigured",
  invalid_checkout_plan: "errors.invalidCheckoutPlan",
  active_subscription_exists: "errors.activeSubscriptionExists",
  no_stripe_customer: "errors.noStripeCustomer",
  invalid_processor: "errors.invalidProcessor",
  processor_not_client_reportable: "errors.processorNotClientReportable",
  storage_limit_exceeded: "errors.storageLimitExceeded",
  document_limit_exceeded: "errors.documentLimitExceeded",
  voice_limit_exceeded: "errors.aiLimitExceeded",
  ai_limit_exceeded: "errors.aiLimitExceeded",
  admin_billing_override_disabled: "errors.adminBillingOverrideDisabled",
  storage_delete_failed: "errors.storageDeleteFailed",
  encrypted_use_blob: "errors.encryptedUseBlob",
  missing_bearer_token: "errors.missingBearerToken",
  invalid_token: "errors.invalidToken",
  auth_not_configured: "errors.authNotConfigured",
  saved_report_limit_exceeded: "errors.savedReportLimitExceeded",
};

const PLAN_KEYS: Record<string, MessageKey> = {
  free: "billing.plan.free",
  personal: "billing.plan.personal",
  family: "billing.plan.family",
  small_business: "billing.plan.smallBusiness",
  business: "billing.plan.business",
};

/** Localize API error params (e.g. subscription plan slug → display name). */
function localizeApiErrorParams(
  params: Record<string, string | number>,
  locale: string | null | undefined,
): Record<string, string | number> {
  if (!("plan" in params)) {
    return params;
  }
  const planKey = PLAN_KEYS[String(params.plan)];
  if (!planKey) {
    return params;
  }
  return {
    ...params,
    plan: translate(planKey, locale),
  };
}

export function translateSubscriptionPlan(
  plan: string,
  locale: string | null | undefined,
): string {
  const planKey = PLAN_KEYS[plan];
  return planKey ? translate(planKey, locale) : plan;
}

export function translateFormat(
  key: MessageKey,
  locale: string | null | undefined,
  values: Record<string, string | number>,
): string {
  let template = translate(key, locale);
  for (const [name, value] of Object.entries(values)) {
    template = template.replaceAll(`{${name}}`, String(value));
  }
  return template;
}

function formatValidationDetail(detail: unknown): string | null {
  if (typeof detail === "string" && detail.trim()) {
    return detail.trim();
  }
  if (!Array.isArray(detail)) {
    return null;
  }
  const messages = detail
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const record = item as { loc?: unknown; msg?: unknown };
      const msg = typeof record.msg === "string" ? record.msg : null;
      if (!msg) return null;
      if (Array.isArray(record.loc) && record.loc.length > 0) {
        return `${record.loc.map(String).join(".")}: ${msg}`;
      }
      return msg;
    })
    .filter((message): message is string => Boolean(message));
  return messages.length > 0 ? messages.join("; ") : null;
}

export function formatApiErrorMessage(
  text: string,
  fallback: string,
  locale: string | null | undefined,
): string {
  const trimmed = text.trim();
  if (!trimmed) {
    return fallback;
  }
  try {
    const json = JSON.parse(trimmed) as { detail?: unknown };
    const detail = json.detail;
    if (detail && typeof detail === "object" && detail !== null && "code" in detail) {
      const record = detail as { code?: string; params?: Record<string, string | number> };
      if (record.code) {
        const localized = formatApiError(record.code, locale, record.params);
        if (localized) {
          return localized;
        }
      }
    }
    const validationMessage = formatValidationDetail(detail);
    if (validationMessage) {
      return validationMessage;
    }
  } catch {
    // plain-text error body
  }
  return trimmed || fallback;
}

export function formatApiError(
  code: string,
  locale: string | null | undefined,
  params?: Record<string, string | number>,
): string | null {
  const key = API_ERROR_KEYS[code];
  if (!key) {
    return null;
  }
  if (params && Object.keys(params).length > 0) {
    return translateFormat(key, locale, localizeApiErrorParams(params, locale));
  }
  return translate(key, locale);
}
